/**
 * δ′.1 — voiceAudioQueue per-sentence MP3 accumulation.
 *
 * Per plan §4 δ′.1 + DESIGN §5.2 (corrected per §22). REPLACES the δ.6
 * per-chunk-decode implementation, which failed in production because
 * `decodeAudioData` requires a complete encoded audio file with valid
 * MP3 header — not arbitrary mid-stream chunks. Kokoro's `stream=true`
 * emits ONE MP3 per sentence (one ID3 header at byte 0), chunked over
 * many WS frames.
 *
 * Architectural contract (load-bearing):
 *   - enqueue(seqPrefixedFrame) — strips 4-byte LE-uint32 seq prefix
 *     and APPENDS the remaining bytes to per-seq accumulator.
 *     Does NOT call decodeAudioData.
 *   - markSentenceComplete(seq) — concatenates the per-seq buffer into
 *     ONE ArrayBuffer (a complete per-sentence MP3 with valid ID3),
 *     calls decodeAudioData(assembled), schedules the resulting
 *     AudioBuffer via AudioBufferSourceNode chain, emits ACK.
 *   - drainAndWait() — resolves on lastScheduledSource.onended OR
 *     close() signal OR hard-timeout escape (R2 NC-2 + NC-3). Rule #19:
 *     no silent hang.
 *   - close() — triggers drainAndWait early-exit, closes AudioContext.
 *
 * Consumer (voiceStore.ts::startTurn) responsibility:
 *   - On chunk_meta(seq=N) arrival → call markSentenceComplete(N-1)
 *     for N>0 (server-side ordering guarantees binary frames for N-1
 *     are on the wire before chunk_meta(N)).
 *   - On end_of_turn → call markSentenceComplete(lastSeq), then
 *     await drainAndWait(), THEN teardownTurn().
 */

const SEQ_HEADER_BYTES = 4;
const UNDERRUN_SILENCE_SECONDS = 0.2;
const _SAMPLE_RATE = 24_000;
const DRAIN_TIMEOUT_HARD_CAP_MS = 10_000;
const DRAIN_TIMEOUT_DURATION_PAD_MS = 2_000;

export type AckCallback = (seq: number) => void;

// W3 (2026-05-25) — D-W3-3 LOCKED. In-app gain control range.
// 0 = mute (gain.value=0 → bit-exact silence per Web Audio API spec)
// 1.0 = native (default; no change from pre-W3 behavior)
// 1.5 = +50% boost ceiling (TTS audio is normalized close to 0dBFS so
// boost above 1.5 risks clipping; if a user reports quiet TTS the right
// fix is server-side normalization, not raising the ceiling).
export const VOICE_AUDIO_VOLUME_MIN = 0;
export const VOICE_AUDIO_VOLUME_MAX = 1.5;

function clampVolume(v: number): number {
  if (!Number.isFinite(v)) return 1.0;
  if (v < VOICE_AUDIO_VOLUME_MIN) return VOICE_AUDIO_VOLUME_MIN;
  if (v > VOICE_AUDIO_VOLUME_MAX) return VOICE_AUDIO_VOLUME_MAX;
  return v;
}

export class VoiceAudioQueue {
  private readonly audioContext: AudioContext;
  private readonly ackCallback: AckCallback;
  /** W3 (2026-05-25) — D-W3-1 LOCKED. Per-instance GainNode inserted between
   * scheduled BufferSources and audioContext.destination. Replaces the
   * pre-W3 direct `src.connect(destination)` routing. Real-time setVolume
   * writes to gainNode.gain.value (D-W3-6 LOCKED — direct assignment is
   * click-free at human slider-drag rates per Web Audio spec). */
  private readonly gainNode: GainNode;
  // Per-seq accumulation: each entry is the list of stripped MP3 byte
  // chunks for that sentence, in arrival order.
  private readonly pendingChunksBySeq: Map<number, ArrayBuffer[]> = new Map();
  // Decoded AudioBuffers awaiting scheduling, keyed by seq for in-order
  // drain via nextSeqToSchedule.
  private readonly pendingByseq: Map<number, AudioBuffer> = new Map();
  private nextSeqToSchedule = 0;
  /** End-time of the last scheduled source, in AudioContext clock. */
  private nextStartTime = 0;
  /** R1 C4 — last AudioBufferSourceNode scheduled. drainAndWait resolves
   * on its onended. Null when nothing has been scheduled yet. */
  private lastScheduledSource: AudioBufferSourceNode | null = null;
  /** R2 NC-3 — pending drainAndWait resolve callback. close() calls it
   * to break out of a never-resolving wait (iOS background, etc). */
  private _drainResolve: (() => void) | null = null;
  /** FA6 (1.158.0) — set of in-flight markSentenceComplete promises.
   * drainAndWait awaits all of these before checking lastScheduledSource,
   * so concurrent decodes from earlier `void mark()` calls complete + drain
   * before the turn tears down. */
  private readonly pendingDecodes: Set<Promise<void>> = new Set();
  private closed = false;

  constructor(
    audioContext: AudioContext,
    ackCallback: AckCallback = () => undefined,
    initialVolume: number = 1.0,
  ) {
    this.audioContext = audioContext;
    this.ackCallback = ackCallback;
    // W3 — create + wire the gainNode → destination once per instance.
    this.gainNode = audioContext.createGain();
    this.gainNode.gain.value = clampVolume(initialVolume);
    this.gainNode.connect(audioContext.destination);
  }

  /**
   * W3 (2026-05-25) — D-W3-3 + D-W3-6 LOCKED. Set the in-app voice volume
   * gain. Clamps to [0, 1.5]. Real-time effect: writing to gain.value
   * applies at the next render quantum (~2-5ms) — inaudible at slider
   * drag rates. Per Web Audio API spec; OPEN-C resolved direct assignment
   * over linearRampToValueAtTime for human input.
   */
  setVolume(value: number): void {
    this.gainNode.gain.value = clampVolume(value);
  }

  /**
   * Accept a single seq-prefixed binary frame. Strips the 4-byte seq
   * prefix and APPENDS the remaining bytes to the per-seq accumulator.
   * Does NOT decode here (per-chunk decode was the δ.8.1 architectural
   * bug; see DESIGN §22).
   */
  enqueue(seqPrefixedFrame: ArrayBuffer): void {
    if (this.closed) return;
    if (seqPrefixedFrame.byteLength < SEQ_HEADER_BYTES) {
      console.warn('[voiceAudioQueue] frame too short for seq prefix:', seqPrefixedFrame.byteLength);
      return;
    }
    const seq = new DataView(seqPrefixedFrame, 0, SEQ_HEADER_BYTES).getUint32(0, true);
    const chunk = seqPrefixedFrame.slice(SEQ_HEADER_BYTES);
    let bucket = this.pendingChunksBySeq.get(seq);
    if (!bucket) {
      bucket = [];
      this.pendingChunksBySeq.set(seq, bucket);
    }
    bucket.push(chunk);
  }

  /**
   * Consumer (voiceStore.startTurn) calls this when sentence `seq` has
   * been fully streamed. Concatenates per-seq accumulator, decodes the
   * COMPLETE per-sentence MP3, schedules the resulting AudioBuffer,
   * emits ACK.
   *
   * Empty-sentence path: if no chunks were enqueued for `seq`, still
   * emit ACK so the server's backpressure counter advances.
   *
   * Decode rejection: build Blob from the assembled buffer + play via
   * HTMLAudioElement (DESIGN §10.6 fallback). ACK still emitted.
   */
  async markSentenceComplete(seq: number): Promise<void> {
    if (this.closed) return;
    const chunks = this.pendingChunksBySeq.get(seq);
    if (!chunks || chunks.length === 0) {
      // Empty sentence (silent failure upstream, or no audio produced).
      // ACK so backpressure advances; nothing to schedule.
      this.ackCallback(seq);
      return;
    }
    this.pendingChunksBySeq.delete(seq);
    const assembled = _concatArrayBuffers(chunks);
    console.warn(`[voiceAudioQueue] markSentenceComplete seq=${seq} bytes=${assembled.byteLength} ctx.state=${this.audioContext.state}`);

    // FA6 (1.158.0) — track this decode+schedule as in-flight so drainAndWait
    // can Promise.all on all in-flight marks before checking lastScheduledSource.
    let trackResolve!: () => void;
    const track = new Promise<void>((res) => { trackResolve = res; });
    this.pendingDecodes.add(track);

    try {
      const buffer = await this.audioContext.decodeAudioData(assembled);
      console.warn(`[voiceAudioQueue] decode OK seq=${seq} duration=${buffer.duration.toFixed(2)}s ctx.state=${this.audioContext.state}`);
      this.pendingByseq.set(seq, buffer);
      await this.drainScheduled();
    } catch (err) {
      console.warn(`[voiceAudioQueue] decode FAIL seq=${seq} fallback to Blob:`, err);
      this.playBlobFallback(assembled, seq, err);
    } finally {
      trackResolve();
      this.pendingDecodes.delete(track);
      this.ackCallback(seq);
    }
  }

  /**
   * Schedule any decoded AudioBuffers in seq order. Called after each
   * successful decode; the loop handles out-of-order decode completions.
   * FA8 (1.158.0) — now async because scheduleBuffer awaits resume().
   */
  private async drainScheduled(): Promise<void> {
    while (this.pendingByseq.has(this.nextSeqToSchedule)) {
      const buf = this.pendingByseq.get(this.nextSeqToSchedule)!;
      this.pendingByseq.delete(this.nextSeqToSchedule);
      await this.scheduleBuffer(buf);
      this.nextSeqToSchedule += 1;
    }
  }

  private async scheduleBuffer(buffer: AudioBuffer): Promise<void> {
    // FA8 (1.158.0) — AWAIT resume before src.start(). Pre-fix this was
    // fire-and-forget which could schedule into a paused timeline on
    // aggressively-suspending browsers (iOS Safari background tab, gesture-
    // context expiry). Defense-in-depth — resume on an already-running
    // context is a no-op per Web Audio spec.
    if (this.audioContext.state === 'suspended') {
      console.warn(`[voiceAudioQueue] AudioContext SUSPENDED at scheduleBuffer — awaiting resume`);
      try {
        await this.audioContext.resume();
      } catch (err) {
        console.error(`[voiceAudioQueue] resume() failed:`, err);
        // Fall through — silence is better than crash. src.start() on a
        // suspended context queues until resume per spec.
      }
    }
    // Underrun bridge: 200ms silence if scheduled-end < currentTime.
    const currentTime = this.audioContext.currentTime;
    if (this.nextStartTime < currentTime) {
      const silenceBuffer = this.audioContext.createBuffer(
        1,
        Math.max(1, Math.floor(_SAMPLE_RATE * UNDERRUN_SILENCE_SECONDS)),
        _SAMPLE_RATE,
      );
      const silenceSrc = this.audioContext.createBufferSource();
      silenceSrc.buffer = silenceBuffer;
      // W3 (2026-05-25) — D-W3-2 LOCKED. Route through gainNode instead of
      // directly to destination. Pre-W3: silenceSrc.connect(audioContext.destination).
      silenceSrc.connect(this.gainNode);
      silenceSrc.start(currentTime);
      this.nextStartTime = currentTime + UNDERRUN_SILENCE_SECONDS;
    }
    const startAt = this.nextStartTime;
    const src = this.audioContext.createBufferSource();
    src.buffer = buffer;
    // W3 — same routing change as the silence-padding source above.
    src.connect(this.gainNode);
    src.start(startAt);
    console.warn(`[voiceAudioQueue] scheduled buffer start=${startAt.toFixed(2)}s duration=${(buffer as AudioBuffer & { duration: number }).duration.toFixed(2)}s ctx.currentTime=${currentTime.toFixed(2)}s ctx.state=${this.audioContext.state}`);
    this.nextStartTime = startAt + (buffer as AudioBuffer & { duration: number }).duration;
    // R1 C4 — track for drainAndWait.
    this.lastScheduledSource = src;
  }

  /**
   * R1 C4 + R2 NC-2 + NC-3 — resolves once all scheduled
   * AudioBufferSourceNodes have finished playback. Called by
   * voiceStore.startTurn's end_of_turn handler BEFORE teardownTurn()
   * so the last sentence's audio finishes playing before the
   * AudioContext closes.
   *
   * Resolution paths (any one resolves):
   *   1. lastScheduledSource.onended fires (normal completion)
   *   2. close() called (early-exit signal)
   *   3. Hard timeout (Rule #19 no silent hang on iOS background)
   */
  async drainAndWait(): Promise<void> {
    if (this.closed) return;

    // 1.159.0 — chunk_meta(N+1) race fix. The chunk_meta handler in
    // voiceStore.ts fires `void mark(N)` when chunk_meta(N+1) arrives. Server
    // FIFO ordering CAN place chunk_meta(N+1) text frame BEFORE all binary
    // frames for seq=N have arrived in the browser's onBinary path. mark(N)
    // then early-returns silently at line 102-107 (empty/partial bucket), and
    // late binary(N) frames go into pendingChunksBySeq[N] but nobody re-fires
    // mark. Result: nextSeqToSchedule stays stuck at N, no audio plays.
    //
    // At drainAndWait time (called by finalizeTurn at end_of_turn), the
    // server's _await_tts_drain has already blocked until ALL TTS tasks
    // completed AND their binary frames are in state.queue. Since end_of_turn
    // is enqueued AFTER all binary frames (single-FIFO sender), by the time
    // client's end_of_turn handler runs, ALL binary frames have been processed
    // by onBinary → enqueue. So any seq still in pendingChunksBySeq has a
    // COMPLETE bucket with a valid ID3 header. Re-fire mark for each such seq.
    for (const seq of [...this.pendingChunksBySeq.keys()]) {
      void this.markSentenceComplete(seq);
    }

    // FA6 (1.158.0) — wait for all in-flight markSentenceComplete promises
    // (decode + drainScheduled) BEFORE checking lastScheduledSource. Loop
    // in case a new mark fires during the await (e.g., a re-fired mark from
    // the chunk_meta(N+1) race fix above). Safety cap prevents pathological
    // infinite loop if marks somehow keep landing.
    let safety = 0;
    while (this.pendingDecodes.size > 0 && safety < 10) {
      await Promise.all([...this.pendingDecodes]);
      safety += 1;
    }

    if (this.lastScheduledSource === null) {
      // Nothing was ever scheduled (every sentence's decode failed and
      // went to Blob fallback, OR no sentences arrived). Resolve now.
      return;
    }
    return new Promise<void>((resolve) => {
      // R10.4 (1.158.0) — timeout from cumulative remaining playback time,
      // not from last buffer's duration alone. nextStartTime equals the
      // scheduled end-time of the last buffer (including any underrun-bridge
      // silences) so (nextStartTime - currentTime) is total remaining audio.
      // Floor at DRAIN_TIMEOUT_DURATION_PAD_MS (2000ms) so the already-finished-
      // audio corner case (nextStartTime ≤ currentTime) still has a sane
      // resolution (onended would have fired but src.onended assignment
      // after-the-fact is a no-op; timeout is sole resolver).
      const remainingMs = (this.nextStartTime - this.audioContext.currentTime) * 1000;
      const timeoutMs = Math.max(
        DRAIN_TIMEOUT_DURATION_PAD_MS,
        Math.min(DRAIN_TIMEOUT_HARD_CAP_MS, remainingMs + DRAIN_TIMEOUT_DURATION_PAD_MS),
      );
      const src = this.lastScheduledSource!;
      // Single-shot resolver: whichever path fires first wins.
      let resolved = false;
      const finish = () => {
        if (resolved) return;
        resolved = true;
        this._drainResolve = null;
        resolve();
      };
      this._drainResolve = finish;
      src.onended = finish;
      setTimeout(finish, timeoutMs);
    });
  }

  private playBlobFallback(assembled: ArrayBuffer, seq: number, err: unknown): void {
    try {
      const blob = new Blob([assembled], { type: 'audio/mpeg' });
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      audio.play().catch((e) => {
        console.warn(`[voiceAudioQueue] Blob fallback play() failed for seq=${seq}:`, e);
      });
    } catch (fallbackErr) {
      console.error(
        `[voiceAudioQueue] decodeAudioData failed AND Blob fallback failed for seq=${seq}:`,
        err,
        fallbackErr,
      );
    }
  }

  /**
   * δ⁗ (2026-05-17) — reset per-turn coordinator state WITHOUT closing the
   * queue or AudioContext. Called from teardownStreamingTurnResources at
   * end-of-turn under PWS. Idempotent.
   *
   * MUST NOT: close audioContext, set closed=true, null audioContext.
   * MUST: clear pendingChunksBySeq + pendingByseq Maps, reset nextSeqToSchedule
   * + nextStartTime, null lastScheduledSource, null _drainResolve.
   *
   * The `_drainResolve` nullification ensures a stale drainAndWait
   * hard-timeout escape from turn N can't fire on turn N+1's drain.
   * The existing `finish` closure's `if (resolved) return` guard at the
   * drainAndWait Promise (line ~188) protects against double-firing of
   * onended after the Promise resolved.
   */
  resetForNextTurn(): void {
    if (this.closed) return;
    // FA6 (1.158.0) — clear pending Promise refs. The Promises themselves
    // resolve in their own time but their results land on an empty
    // pendingByseq and short-circuit (drainScheduled finds nothing). Safe.
    this.pendingDecodes.clear();
    this.pendingChunksBySeq.clear();
    this.pendingByseq.clear();
    this.nextSeqToSchedule = 0;
    this.nextStartTime = 0;
    this.lastScheduledSource = null;
    this._drainResolve = null;
  }

  /**
   * Phase 9.7 (2026-05-24) — D-9.7-3 mid-turn cancellation. Idempotent.
   *
   * Signals any in-flight `drainAndWait()` to resolve immediately + clears
   * pending Maps + resets state. UNLIKE `close()`, the AudioContext is
   * preserved (D-9.7-4 WS stays open ⇒ audio queue stays alive for the
   * next turn on the same PWS connection). UNLIKE `resetForNextTurn()`,
   * this fires the `_drainResolve` callback so a concurrent drainAndWait
   * Promise resolves without the hard-timeout escape.
   *
   * Called by voiceStore's `cancelled` frame handler — after the backend
   * confirms the cancel landed and emits the cancelled frame.
   *
   * Stops any currently-playing audio by disconnecting + nullifying
   * lastScheduledSource (the AudioBufferSourceNode for the in-flight
   * sentence). Pending decodes finish in their own time but land on an
   * empty pendingByseq and short-circuit (same safety as resetForNextTurn).
   */
  abort(): void {
    if (this.closed) return;
    // Stop any in-flight playback. Source nodes are one-shot; stop() throws
    // InvalidStateError if not yet started — wrap defensively.
    if (this.lastScheduledSource) {
      try {
        this.lastScheduledSource.stop();
      } catch {
        /* not started yet — fine */
      }
      try {
        this.lastScheduledSource.disconnect();
      } catch {
        /* already disconnected */
      }
      this.lastScheduledSource = null;
    }
    // Signal any pending drain to resolve (same as close path).
    if (this._drainResolve) {
      this._drainResolve();
      this._drainResolve = null;
    }
    // Clear pending state (same as resetForNextTurn) but PRESERVE AudioContext.
    this.pendingDecodes.clear();
    this.pendingChunksBySeq.clear();
    this.pendingByseq.clear();
    this.nextSeqToSchedule = 0;
    this.nextStartTime = 0;
  }

  /**
   * Tear down. Idempotent. Triggers drainAndWait early-exit via
   * `_drainResolve` so a concurrent drainAndWait Promise resolves.
   */
  close(): void {
    if (this.closed) return;
    this.closed = true;
    // R2 NC-3 — signal any pending drainAndWait BEFORE closing the context.
    if (this._drainResolve) {
      this._drainResolve();
      this._drainResolve = null;
    }
    this.pendingChunksBySeq.clear();
    this.pendingByseq.clear();
    this.lastScheduledSource = null;
    try {
      const closePromise = this.audioContext.close();
      if (closePromise && typeof closePromise.then === 'function') {
        closePromise.catch(() => {
          /* already closed */
        });
      }
    } catch {
      /* sync throw on already-closed */
    }
  }
}

function _concatArrayBuffers(chunks: ArrayBuffer[]): ArrayBuffer {
  let total = 0;
  for (const c of chunks) total += c.byteLength;
  const out = new ArrayBuffer(total);
  const view = new Uint8Array(out);
  let offset = 0;
  for (const c of chunks) {
    view.set(new Uint8Array(c), offset);
    offset += c.byteLength;
  }
  return out;
}
